import React from "react";
import ContentContainer from "./contentContainer";
import { Box, Grid2 } from "@mui/material";
import PageTitle from "./pageTitle";
import { useReleaseNotes2 } from "../../fetch/fetchReleaseNotes";
import './g3ReleaseNotes.css';
import dayjs from "dayjs";
import ReleaseNoteDocumentLink from "./releaseNoteDocumentLink";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer';

export default function G3ReleaseNotes() {
    const { items, isLoading } = useReleaseNotes2();

    const isVideoEntry = (item) => {
        return !!item && item.trim().startsWith('{') && item.trim().endsWith('}');
    }

    return (
        <ContentContainer>
            <PageTitle title='Release Notes' />

            <Box component="main" sx={{ p: 3, width: '100%', paddingTop: 0 }}>
                <Grid2 container>
                    {!isLoading ? items.map((item, index) => {
                        return (
                            <Grid2 item size={12} key={index} style={{ margin: '0 1em' }}>
                                <div className="g3-note-version-header">
                                    <h4>
                                        <span>{item.title}</span>
                                        <span className="g3-note-version-header-release-date">{`Released: ${dayjs(item.releaseDate).format('MM/DD/YYYY')}`}</span>
                                        <span className="g3-note-version-header-release-date">
                                            <PDFDownloadLink document={<ItemPdf item={item} />} fileName={`${item.title.replace(' ', '_')}.pdf`}>
                                                {({ blob, url, loading, error }) =>
                                                    loading ? '' : 'Download'
                                                }
                                            </PDFDownloadLink>
                                        </span>
                                    </h4>
                                </div>
                                <div style={{ marginTop: '1em' }}>
                                    {item.relatedAdvisories && item.relatedAdvisories.length > 0 ? (
                                        <div>
                                            <b>Advisory: </b>
                                            {item.relatedAdvisories.map((partNumber, index) => (
                                                <ReleaseNoteDocumentLink key={index} partNumber={partNumber} />
                                            ))}
                                        </div>
                                    ) : null}

                                    {item.relatedVideos && item.relatedVideos.length > 0 ? (
                                        <div>
                                            <b>Videos: </b>
                                            {item.relatedVideos.map((partNumber, index) => (
                                                <ReleaseNoteDocumentLink key={index} partNumber={partNumber} isVideo />
                                            ))}
                                        </div>
                                    ) : null}

                                    {item.newFeatures && item.newFeatures.length > 0 ? (
                                        <div className="g3-note-version-section">
                                            <h5 className="g3-note-version-section-title">New Features</h5>
                                            <ul>
                                                {item.newFeatures.map((feature, index) =>
                                                    !isVideoEntry(feature) ? (
                                                        <li key={index}>{feature}</li>
                                                    ) : (
                                                        <VideoListItem key={index} item={feature} />
                                                    ))}
                                            </ul>
                                        </div>
                                    ) : null}

                                    {item.changes && item.changes.length > 0 ? (
                                        <div className="g3-note-version-section">
                                            <h5 className="g3-note-version-section-title">Changes</h5>
                                            <ul>
                                                {item.changes.map((issue, index) =>
                                                    !isVideoEntry(issue) ? (
                                                        <li key={index}>{issue}</li>
                                                    ) : (
                                                        <VideoListItem key={index} item={issue} />
                                                    ))}
                                            </ul>
                                        </div>
                                    ) : null}

                                    {item.resolvedIssues && item.resolvedIssues.length > 0 ? (
                                        <div className="g3-note-version-section">
                                            <h5 className="g3-note-version-section-title">Resolved Issues</h5>
                                            <ul>
                                                {item.resolvedIssues.map((issue, index) =>
                                                    !isVideoEntry(issue) ? (
                                                        <li key={index}>{issue}</li>
                                                    ) : (
                                                        <VideoListItem key={index} item={issue} />
                                                    ))}
                                            </ul>
                                        </div>
                                    ) : null}
                                </div>
                            </Grid2>
                        );
                    }) : <span>Loading...</span>}
                </Grid2>
            </Box>
        </ContentContainer>
    );
}

function VideoListItem({ item: json }) {
    const item = JSON.parse(json);
    const { title, partNumber } = item;
    return (
        <li>
            {title}&nbsp;
            <b>Related Video:</b> <ReleaseNoteDocumentLink partNumber={partNumber} isVideo />
        </li>
    );
}

const styles = StyleSheet.create({
    page: {
        paddingTop: 35,
        paddingBottom: 65,
        paddingHorizontal: 35
    },
    pageTitle: {
        marginBottom: 10,
        fontSize: '18pt',
        textAlign: 'left',
        borderBottom: '2px solid #c00'
    },
    pageTitleSubText: {
        fontSize: '12pt'
    },
    sectionTitle: {
        marginTop: 10,
        marginBottom: 10,
        marginHorizontal: 25,
        fontSize: 16,
        marginBottom: 10,
        borderBottom: '1px solid #000'
    },
    listItem: {
        flexDirection: 'row',
        marginBottom: 4,
        marginHorizontal: 30
    },
    bullet: {
        width: 10,
        fontSize: 12,
    },
    text: {
        fontSize: 12,
    },
    linkText: {
        fontSize: 12,
        color: 'blue',
    },
    boldText: {
        fontSize: 12,
        fontWeight: 'bold',
    },
});

export const ItemPdf = ({ item }) => {
    const isVideoEntry = (item) => {
        return !!item && item.trim().startsWith('{') && item.trim().endsWith('}');
    }

    return (
        <Document title={`${item.title} Release Notes`}>
            <Page style={styles.page} size="A4">
                <Text style={styles.pageTitle}>
                    <Text>
                        {item.title}{'      '}
                    </Text>
                    <Text style={styles.pageTitleSubText}>{`Released: ${dayjs(item.releaseDate).format('MM/DD/YYYY')}`}</Text>
                </Text>

                {item.advisories && item.advisories.length > 0 ? (
                    <View>
                        <Text style={styles.sectionTitle}>Advisory</Text>
                        <View>
                            {item.advisories.map((advisory, index) => (
                                <View key={index} style={styles.listItem}>
                                    <Text style={styles.bullet}>•</Text>
                                    <Text style={styles.text}>
                                        <Link src={`${window.location.origin}/literature/view/${advisory.partNumber}`} style={{ color: 'blue' }}>
                                            {advisory.title}
                                        </Link>
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </View>
                ) : null}

                {item.videos && item.videos.length > 0 ? (
                    <View>
                        <Text style={styles.sectionTitle}>Videos</Text>
                        <View>
                            {item.videos.map((video, index) => (
                                <View key={index} style={styles.listItem}>
                                    <Text style={styles.bullet}>•</Text>
                                    <Text style={styles.text}>
                                        <Link src={`${window.location.origin}/literature/video/${video.partNumber}`} style={{ color: 'blue' }}>
                                            {video.title}
                                        </Link>
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </View>
                ) : null}

                {item.newFeatures && item.newFeatures.length > 0 ? (
                    <View>
                        <Text style={styles.sectionTitle}>New Features</Text>
                        <View>
                            {item.newFeatures.map((feature, index) => !isVideoEntry(feature) ?
                                <View key={index} style={styles.listItem}>
                                    <Text style={styles.bullet}>•</Text>
                                    <Text style={styles.text}>{feature}</Text>
                                </View>
                                : <ItemPdfVideoListItem key={index} item={feature} />)}
                        </View>
                    </View>
                ) : null}
                {item.changes && item.changes.length > 0 ? (
                    <View>
                        <Text style={styles.sectionTitle}>New Features</Text>
                        <View>
                            {item.changes.map((change, index) => (
                                <View key={index} style={styles.listItem}>
                                    <Text style={styles.bullet}>•</Text>
                                    <Text style={styles.text}>{change}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                ) : null}
                {item.resolvedIssues && item.resolvedIssues.length > 0 ? (
                    <View>
                        <Text style={styles.sectionTitle}>New Features</Text>
                        <View>
                            {item.resolvedIssues.map((issue, index) => (
                                <View key={index} style={styles.listItem}>
                                    <Text style={styles.bullet}>•</Text>
                                    <Text style={styles.text}>{issue}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                ) : null}
            </Page>
        </Document>
    );
}

const ItemPdfVideoListItem = ({ item: json }) => {
    const item = JSON.parse(json);
    const { title, partNumber } = item;
    const url = `${window.location.origin}/literature/video/${partNumber}`;

    return (
        <View style={styles.listItem}>
            <Text>
                <Text style={styles.bullet}>• </Text>
                <Text style={styles.text}>{title} </Text>
                <Text style={styles.boldText}>Related Video: </Text>
                <Link src={url} style={styles.linkText}>
                    View
                </Link>
            </Text>
        </View>
    );
}